import { Column, Index } from 'typeorm';
import type { BookingStatus } from '../../constants/booking';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

/**
 * PD-17646 — a meeting a guest booked. The guest's details are kept as they were given;
 * the person and account they matched to are filled in after booking, because that work
 * and the emails happen in the background.
 */
@Index('IDX_booking_host_start', ['fkUserIdHost', 'startDate'])
@Index('IDX_booking_type_start', ['fkBookingTypeId', 'startDate'])
@Index('IDX_booking_manage_token', ['manageTokenHash'], { unique: true })
@Index('IDX_booking_contact', ['fkContactId'])
@Index('IDX_booking_event', ['fkEventId'])
@Entity('booking', {}, { auditRedactedProperties: ['manageNonce', 'manageTokenHash'] })
export class BookingEntity extends BaseEntity {
	@Column('int', { name: 'fk_booking_type__id', unsigned: true })
	public fkBookingTypeId!: number;

	@Column('int', { name: 'fk_user__id_host', unsigned: true })
	public fkUserIdHost!: number;

	@Column('datetime', { name: 'startDate' })
	public startDate!: Date;

	@Column('datetime', { name: 'endDate' })
	public endDate!: Date;

	/** PD-17649 — which table at the booth; null for any other meeting. */
	@Column('int', { name: 'tableNo', unsigned: true, nullable: true })
	public tableNo!: number | null;

	@Column('varchar', { name: 'status', length: 16, default: 'booked' })
	public status!: BookingStatus;

	@Column('varchar', { name: 'guestName', length: 120 })
	public guestName!: string;

	@Column('varchar', { name: 'guestCompany', length: 160, nullable: true })
	public guestCompany!: string | null;

	@Column('varchar', { name: 'guestEmail', length: 320 })
	public guestEmail!: string;

	@Column('varchar', { name: 'guestPhone', length: 32, nullable: true })
	public guestPhone!: string | null;

	/** The guest's time zone, for the times in their emails. */
	@Column('varchar', { name: 'guestTimezone', length: 64, nullable: true })
	public guestTimezone!: string | null;

	/** Answers by question id. Outside text: never treated as instructions or markup. */
	@Column('json', { name: 'answers', nullable: true })
	public answers!: Record<string, string> | null;

	/**
	 * What the reschedule and cancel link's token is worked out from, with the server's
	 * secret, so each email can carry the same link without the token being stored.
	 */
	@Column('varchar', { name: 'manageNonce', length: 32 })
	public manageNonce!: string;

	/** The hash a manage link's token is looked up by. */
	@Column('varchar', { name: 'manageTokenHash', length: 128 })
	public manageTokenHash!: string;

	/** The company whose brand the page showed, for the emails. */
	@Column('int', { name: 'fk_company__id', unsigned: true, nullable: true })
	public fkCompanyId!: number | null;

	/** The personal link this was booked from, when it was one. */
	@Column('int', { name: 'fk_booking_invite__id', unsigned: true, nullable: true })
	public fkBookingInviteId!: number | null;

	@Column('int', { name: 'fk_contact__id', unsigned: true, nullable: true })
	public fkContactId!: number | null;

	@Column('int', { name: 'fk_client__id', unsigned: true, nullable: true })
	public fkClientId!: number | null;

	/** The task that puts the meeting on the host's calendar and the account's history. */
	@Column('int', { name: 'fk__event__id', unsigned: true, nullable: true })
	public fkEventId!: number | null;

	@Column('int', { name: 'fk__event_occurrence__id', unsigned: true, nullable: true })
	public fkEventOccurrenceId!: number | null;

	/** The place written to the host's calendar. */
	@Column('varchar', { name: 'calendarLocation', length: 255, nullable: true })
	public calendarLocation!: string | null;

	/** The calendar entry is a Teams meeting. */
	@Column({ type: 'boolean', name: 'isOnlineMeeting', width: 1, default: () => "'0'" })
	public isOnlineMeeting!: boolean;

	/** Teams join link, once the meeting is on the host's Outlook calendar. */
	@Column('varchar', { name: 'joinUrl', length: 1024, nullable: true })
	public joinUrl!: string | null;

	/** Goes up with each change, so calendar apps replace the invite rather than add one. */
	@Column('int', { name: 'inviteSequence', unsigned: true, default: 0 })
	public inviteSequence!: number;

	/** The `inviteSequence` the guest's last email went out for; null before the first. */
	@Column('int', { name: 'emailedSequence', unsigned: true, nullable: true })
	public emailedSequence!: number | null;

	@Column('datetime', { name: 'reminderEmailDate', nullable: true })
	public reminderEmailDate!: Date | null;

	@Column('datetime', { name: 'reminderTextDate', nullable: true })
	public reminderTextDate!: Date | null;

	@Column('datetime', { name: 'cancelledDate', nullable: true })
	public cancelledDate!: Date | null;
}
